(function () {
  if (!window.customElements || customElements.get('cart-drawer')) return;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.host = this.closest('.custom-header');
      this.panel = this.querySelector('[data-cart-panel]');
      this.opener = null;
      this.busy = false;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
      if (this.host) this.host.classList.remove('is-cart-open');
      this.unlockScroll();
    }

    get isOpen() {
      return !this.hidden;
    }

    onDocumentClick(event) {
      const opener = event.target.closest ? event.target.closest('[data-cart-open]') : null;
      if (!opener) return;

      event.preventDefault();
      this.opener = opener;
      this.open();
    }

    onClick(event) {
      if (!event.target.closest) return;

      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const change = event.target.closest('[data-cart-change]');
      if (change) {
        event.preventDefault();
        this.change(change.getAttribute('data-cart-line'), change.getAttribute('data-cart-quantity'), change.href);
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.panel) return;

      const items = Array.prototype.slice.call(this.panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetWidth > 0 || el.offsetHeight > 0;
      });
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      if (this.isOpen && this.classList.contains('is-open')) return;

      this.hidden = false;
      if (this.host) this.host.classList.add('is-cart-open');
      this.lockScroll();

      requestAnimationFrame(() => {
        this.classList.add('is-open');
        const focusTarget = this.panel ? this.panel.querySelector(FOCUSABLE) : null;
        if (focusTarget) focusTarget.focus();
      });
    }

    close() {
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      this.unlockScroll();

      this.closeTimer = window.setTimeout(() => {
        this.hidden = true;
        this.closeTimer = null;
        if (this.host) this.host.classList.remove('is-cart-open');
      }, 320);

      if (this.opener && document.contains(this.opener)) {
        this.opener.focus();
        this.opener = null;
      }
    }

    change(line, quantity, fallbackUrl) {
      if (this.busy || line === null || quantity === null) return;

      this.setBusy(true);

      const body = {
        line: Number(line),
        quantity: Number(quantity),
      };

      if (this.sectionId) {
        body.sections = this.sectionId;
        body.sections_url = window.location.pathname;
      }

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart/change.js'
        : '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => {
          if (!response.ok) throw new Error('cart');
          return response.json();
        })
        .then((cart) => {
          this.updateCount(cart.item_count);

          const markup = cart.sections && this.sectionId ? cart.sections[this.sectionId] : null;
          if (markup) {
            this.replaceContents(markup);
            this.setBusy(false);
            return null;
          }

          return this.fetchSection().then((html) => {
            if (html) this.replaceContents(html);
            this.setBusy(false);
            return null;
          });
        })
        .catch(() => {
          this.setBusy(false);
          if (fallbackUrl) window.location.href = fallbackUrl;
        });
    }

    fetchSection() {
      if (!this.sectionId) return Promise.resolve(null);

      return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(this.sectionId))
        .then((response) => (response.ok ? response.text() : null))
        .catch(() => null);
    }

    replaceContents(markup) {
      const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-cart-inner]');
      const current = this.querySelector('[data-cart-inner]');
      if (!fresh || !current) return;

      current.innerHTML = fresh.innerHTML;
      this.panel = this.querySelector('[data-cart-panel]');

      const focusTarget = this.panel ? this.panel.querySelector(FOCUSABLE) : null;
      if (focusTarget && this.isOpen) focusTarget.focus();
    }

    updateCount(count) {
      const nodes = document.querySelectorAll('[data-cart-count]');
      Array.prototype.forEach.call(nodes, function (node) {
        node.textContent = count;
        node.hidden = count === 0;
      });
    }

    setBusy(state) {
      this.busy = state;
      if (state) {
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute('aria-busy');
      }
    }

    lockScroll() {
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      if (this.previousOverflow === undefined) return;
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
